<script setup>
/* eslint-disable vue/no-root-v-if */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import PlatformAnnouncementsAPI from 'dashboard/api/platformAnnouncements';
import { HOME_PRIMARY_LINK_CLASS } from './homeTones';

const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();

const announcements = ref([]);
const activeIndex = ref(0);
const isLoading = ref(true);
const expandedImage = ref(null);

const CATEGORY_KEYS = {
  promotion: 'PROMOTION',
  news: 'NEWS',
  tip: 'TIP',
  improvement: 'IMPROVEMENT',
  maintenance: 'MAINTENANCE',
};

const categoryClasses = {
  promotion: 'bg-n-amber-3 text-n-amber-11',
  news: 'bg-n-blue-3 text-n-blue-11',
  tip: 'bg-n-teal-3 text-n-teal-11',
  improvement: 'bg-n-violet-3 text-n-violet-11',
  maintenance: 'bg-n-slate-3 text-n-slate-11',
};

const currentAnnouncement = computed(
  () => announcements.value[activeIndex.value]
);
const hasMultipleAnnouncements = computed(() => announcements.value.length > 1);

const categoryLabel = category =>
  t(
    `OPERATIONAL_HOME.ANNOUNCEMENTS.CATEGORY.${CATEGORY_KEYS[category] || 'DEFAULT'}`
  );

const categoryClass = category =>
  categoryClasses[category] || categoryClasses.maintenance;

const formatDate = value =>
  new Intl.DateTimeFormat(locale.value === 'en' ? 'en' : 'pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const selectAnnouncement = index => {
  activeIndex.value = index;
};

const showPrevious = () => {
  activeIndex.value =
    (activeIndex.value - 1 + announcements.value.length) %
    announcements.value.length;
};

const showNext = () => {
  activeIndex.value = (activeIndex.value + 1) % announcements.value.length;
};

const markCurrentAsRead = async () => {
  const announcement = currentAnnouncement.value;
  if (!announcement || announcement.read_at) return;

  try {
    await PlatformAnnouncementsAPI.read(announcement.id);
    announcement.read_at = new Date().toISOString();
  } catch {
    // A navegação não deve ser bloqueada quando a confirmação de leitura falhar.
  }
};

const openImage = () => {
  if (!currentAnnouncement.value?.image_url) return;
  expandedImage.value = {
    url: currentAnnouncement.value.image_url,
    alt: currentAnnouncement.value.title,
  };
};

const closeImage = () => {
  expandedImage.value = null;
};

const handleKeydown = event => {
  if (event.key === 'Escape') closeImage();
};

const loadAnnouncements = async () => {
  try {
    const { data } = await PlatformAnnouncementsAPI.get();
    announcements.value = (data.payload || [])
      .filter(item => !item.dismissed_at)
      .slice(0, 3);
  } catch {
    announcements.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadAnnouncements();
  window.addEventListener('keydown', handleKeydown);
});
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <section
    v-if="isLoading || announcements.length"
    class="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm dark:border-n-strong"
    :aria-label="t('OPERATIONAL_HOME.ANNOUNCEMENTS.ARIA_LABEL')"
  >
    <header class="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
      <div class="flex min-w-0 items-center gap-2.5">
        <span
          class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-brand text-white"
          aria-hidden="true"
        >
          <span class="i-hugeicons-megaphone-01 size-4" />
        </span>
        <div class="min-w-0">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.TITLE') }}
          </h2>
          <p class="mb-0 truncate text-xs text-n-slate-11">
            {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-1.5">
        <template v-if="hasMultipleAnnouncements">
          <button
            type="button"
            class="grid size-8 place-items-center rounded-lg border border-n-weak text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong"
            :aria-label="t('OPERATIONAL_HOME.ANNOUNCEMENTS.PREVIOUS')"
            @click="showPrevious"
          >
            <span class="i-hugeicons-arrow-left-01 size-4" />
          </button>
          <button
            type="button"
            class="grid size-8 place-items-center rounded-lg border border-n-weak text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong"
            :aria-label="t('OPERATIONAL_HOME.ANNOUNCEMENTS.NEXT')"
            @click="showNext"
          >
            <span class="i-hugeicons-arrow-right-01 size-4" />
          </button>
        </template>
        <router-link
          :to="accountScopedRoute('platform_announcements')"
          class="ml-1 inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-n-brand transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        >
          {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.VIEW_ALL') }}
          <span class="i-hugeicons-arrow-up-right-01 size-3.5" />
        </router-link>
      </div>
    </header>

    <div v-if="isLoading" class="grid gap-3 p-5" aria-busy="true">
      <div class="h-4 w-24 rounded bg-n-alpha-2 motion-safe:animate-pulse" />
      <div class="h-6 w-1/2 rounded bg-n-alpha-2 motion-safe:animate-pulse" />
      <div class="h-4 w-3/4 rounded bg-n-alpha-2 motion-safe:animate-pulse" />
    </div>

    <article
      v-else-if="currentAnnouncement"
      :key="currentAnnouncement.id"
      class="grid min-w-0 flex-1 gap-4 p-5 md:grid-cols-[minmax(0,1fr)_minmax(11rem,34%)]"
    >
      <div class="flex min-w-0 flex-col justify-between gap-4">
        <div class="min-w-0">
          <div class="mb-2.5 flex flex-wrap items-center gap-2">
            <span
              class="rounded-md px-2 py-0.5 text-xs font-medium"
              :class="categoryClass(currentAnnouncement.category)"
            >
              {{ categoryLabel(currentAnnouncement.category) }}
            </span>
            <span
              v-if="!currentAnnouncement.read_at"
              class="inline-flex items-center gap-1.5 rounded-md bg-n-brand px-2 py-0.5 text-xs font-semibold text-white"
            >
              <span class="size-1.5 rounded-full bg-current" />
              {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.NEW') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ formatDate(currentAnnouncement.published_at) }}
            </span>
          </div>
          <h3
            class="mb-2 text-lg font-semibold leading-snug text-n-slate-12 md:text-xl"
          >
            {{ currentAnnouncement.title }}
          </h3>
          <div
            v-dompurify-html="currentAnnouncement.body"
            class="announcement-preview line-clamp-3 text-sm leading-6 text-n-slate-11"
          />
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <a
            v-if="
              currentAnnouncement.action_label && currentAnnouncement.action_url
            "
            :href="currentAnnouncement.action_url"
            target="_blank"
            rel="noopener noreferrer"
            :class="HOME_PRIMARY_LINK_CLASS"
            @click="markCurrentAsRead"
          >
            {{ currentAnnouncement.action_label }}
            <span class="i-hugeicons-link-square-02 size-4" />
          </a>
          <router-link
            v-else
            :to="accountScopedRoute('platform_announcements')"
            :class="HOME_PRIMARY_LINK_CLASS"
            @click="markCurrentAsRead"
          >
            {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.READ') }}
            <span class="i-hugeicons-arrow-right-01 size-4" />
          </router-link>

          <div
            v-if="hasMultipleAnnouncements"
            class="ml-auto flex items-center gap-1.5"
            :aria-label="t('OPERATIONAL_HOME.ANNOUNCEMENTS.POSITION')"
          >
            <button
              v-for="(_, index) in announcements"
              :key="index"
              type="button"
              class="h-2 rounded-full transition-all motion-reduce:transition-none"
              :class="
                index === activeIndex
                  ? 'w-6 bg-n-brand'
                  : 'w-2 bg-n-slate-6 hover:bg-n-slate-8'
              "
              :aria-label="
                t('OPERATIONAL_HOME.ANNOUNCEMENTS.SHOW_ITEM', {
                  index: index + 1,
                })
              "
              :aria-current="index === activeIndex ? 'true' : undefined"
              @click="selectAnnouncement(index)"
            />
          </div>
        </div>
      </div>

      <button
        v-if="currentAnnouncement.image_url"
        type="button"
        class="group relative block h-44 min-w-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong md:h-full md:min-h-44"
        :aria-label="
          t('OPERATIONAL_HOME.ANNOUNCEMENTS.EXPAND_IMAGE', {
            title: currentAnnouncement.title,
          })
        "
        @click="openImage"
      >
        <img
          :src="currentAnnouncement.image_url"
          :alt="currentAnnouncement.title"
          class="size-full max-h-64 object-contain transition-transform duration-200 motion-safe:group-hover:scale-[1.02]"
        />
        <span
          class="absolute bottom-2 right-2 flex items-center gap-1.5 rounded-lg bg-n-slate-12 px-2 py-1 text-xs font-medium text-n-slate-1 shadow-sm"
        >
          <span class="i-hugeicons-zoom-in-area size-3.5" />
          {{ t('OPERATIONAL_HOME.ANNOUNCEMENTS.EXPAND') }}
        </span>
      </button>
      <div
        v-else
        class="hidden place-items-center rounded-xl border border-n-weak bg-n-solid-2 text-n-brand dark:border-n-strong md:grid"
        aria-hidden="true"
      >
        <span class="i-hugeicons-megaphone-01 size-12" />
      </div>
    </article>

    <Teleport to="body">
      <div
        v-if="expandedImage"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 md:p-8"
        role="dialog"
        aria-modal="true"
        :aria-label="
          t('OPERATIONAL_HOME.ANNOUNCEMENTS.EXPANDED_IMAGE', {
            title: expandedImage.alt,
          })
        "
        @click.self="closeImage"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-10 grid size-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white md:right-6 md:top-6"
          :aria-label="t('OPERATIONAL_HOME.ANNOUNCEMENTS.CLOSE_IMAGE')"
          @click="closeImage"
        >
          <span class="i-hugeicons-cancel-01 size-6" />
        </button>
        <img
          :src="expandedImage.url"
          :alt="expandedImage.alt"
          class="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          @click.stop
        />
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.announcement-preview :deep(p) {
  margin: 0;
}
</style>
